import * as Dialog from '@radix-ui/react-dialog';
import { forwardRef, type ReactNode } from 'react';

import { IconButton } from '../../server/IconButton/IconButton';
import { classNames } from '../../server/utils/classNames';

export type CloseButtonColorScheme = 'default' | 'light' | 'dark';

export type ModalContentProps = {
	children: ReactNode;
	className?: string;
	closeButtonColorScheme?: CloseButtonColorScheme;
};

export const ModalContent = forwardRef<HTMLDivElement, ModalContentProps>((props, ref) => {
	const { children, className, closeButtonColorScheme = 'default' } = props;
	return (
		<Dialog.Portal>
			<Dialog.Overlay
				data-testid="modal-overlay"
				className={classNames(
					'fixed w-full bg-shade-primary-600 inset-0 flex flex-col items-center justify-center z-10000',
					'data-[state=closed]:animate-full-page-modal-overlay-fade-out sm:data-[state=closed]:animate-none',
				)}>
				<Dialog.Content
					ref={ref}
					onInteractOutside={event => {
						// Prevent modal from closing when clicking on the toast that appears on top of the modal.
						const targetElement = event.target as Element;
						if (targetElement.closest('[data-id=toast]')) {
							event.preventDefault();
						}
					}}
					className={classNames(
						'flex bg-bg-primary flex-col w-full h-full sm:h-auto focus:outline-hidden text-content-primary',
						'sm:rounded-xl sm:shadow-200 sm:max-w-layout-compact sm:overflow-hidden',
						'data-[state=closed]:animate-full-page-modal-content-fade-out sm:data-[state=closed]:animate-none',
						'animate-full-page-modal-content-fade-in sm:animate-none',
						'data-[state=open]:opacity-0 sm:data-[state=open]:opacity-100',
						className,
					)}
					// aria-describedby={undefined} is required for Radix Dialog to indicate that no description is provided.
					aria-describedby={undefined}>
					<div className="w-full h-full flex flex-col">
						<header className="rounded-t-lg pt-3 pr-3 w-full flex justify-end">
							<Dialog.Close asChild>
								<IconButton
									analyticsId={null}
									data-testid="modal-close-button"
									icon="close"
									ariaLabel="Close"
									className={classNames('text-content-primary', {
										dark: closeButtonColorScheme === 'light',
										light: closeButtonColorScheme === 'dark',
									})}
								/>
							</Dialog.Close>
						</header>
						{children}
					</div>
				</Dialog.Content>
			</Dialog.Overlay>
		</Dialog.Portal>
	);
});
